import {Actions, Context} from "vuex-smart-module";
import {AxiosError, AxiosResponse} from "axios";
import {Store} from "vuex";
import AccessControlState, {AccessControlStatus} from "@/store/modules/access-control/access-control-state";
import AccessControlGetters from "@/store/modules/access-control/access-control-getters";
import AccessControlMutations from "@/store/modules/access-control/access-control-mutations";
import esquireClient from "@/modules/esquire-client";
import AccessControlDeviceData, {IAccessControlDeviceData} from "@/http/data/access-control-device-data";
import AccessControlCardData from "@/http/data/access-control-card-data";
import AccessControlEventPageData from "@/http/data/access-control-event-page-data";
import AccessControlDoorPasscodesData from "@/http/data/access-control-door-passcodes-data";
import AccessControlDoorUnlockData from "@/http/data/access-control-door-unlock-data";
import AccessControlDoorData, {IAccessControlDoorData} from "@/http/data/access-control-door-data";
import BadResponseData from "@/http/data/bad-response-data";
import {
    AccessControlCardPayload,
    AccessControlCreateCardPayload,
    AccessControlDevicePayload,
    AccessControlDoorPasscodesPayload,
    AccessControlDoorPayload,
    AccessControlEventsPayload,
    AccessControlUpdateCardPayload,
    AccessControlUpdateDevicePayload,
    AccessControlUpdateDoorPayload
} from "@/store/modules/access-control/access-control-action-payloads";
import db from "@/modules/vanity-db";
import {store} from "@/store";
import root from "@/store/modules";

interface AccessControlErrorResponse {
    code?: string;
    message?: string;
}

interface AccessControlUpsertCardPayload {
    deviceId: number;
    card: AccessControlCardData;
}

export default class AccessControlActions extends Actions<
    AccessControlState,
    AccessControlGetters,
    AccessControlMutations,
    AccessControlActions
> {
    private rootContext!: Context<typeof root>;

    $init(_store: Store<typeof store>): void {
        this.rootContext = root.context(_store);

        super.$init(_store);
    }

    private async handleAuthStatus(status: number): Promise<boolean> {
        if (status === 401) {
            this.mutations.setAccessControlStatus(AccessControlStatus.Unauthenticated);

            await this.rootContext.modules.user.actions.logout();

            const routerModule = await import("@/router");

            await routerModule.default.push({name: "Login"});
            return true;
        }

        return false;
    }

    private async notifyClientError(response: AxiosResponse): Promise<void> {
        const error = new BadResponseData(response.data);

        await this.rootContext.actions.message({
            message: error.message,
            type: "error",
            duration: 0,
            showClose: true
        });
    }

    private async notifyServerError(response: AxiosResponse): Promise<void> {
        await this.rootContext.actions.message({
            message: `${response.status} ${response.statusText}`,
            type: "error",
            duration: 0,
            showClose: true
        });
    }

    private async handleErrorResponse(response: AxiosResponse): Promise<void> {
        if (response.status >= 400 && response.status < 500) {
            await this.actions.notifyClientError(response);
            return;
        }
        if (response.status >= 500) {
            await this.actions.notifyServerError(response);
        }
    }

    private upsertCard(payload: AccessControlUpsertCardPayload): void {
        const {deviceId, card} = payload;
        const previousCard = this.state.cardDetailsByDeviceId[deviceId]?.[card.card_number];
        const cardNumbers = this.state.cardNumbersByDeviceId[deviceId] ?? [];

        if (!cardNumbers.includes(card.card_number)) {
            this.mutations.setCardNumbersForDevice({
                deviceId,
                cardNumbers: [...cardNumbers, card.card_number]
            });
        }

        this.mutations.setCardDetails({deviceId, card});
        this.rootContext.modules.unit.mutations.setAccessControlDeviceCardUnit({
            cardNumber: card.card_number,
            previousUnitId: previousCard?.unit_id ?? null,
            unitId: card.unit_id
        });
    }

    async attemptRestoreSelectedDevice(): Promise<void> {
        const selectedDevice = await db.selected.get("access-control-device");

        if (!selectedDevice) return;

        this.mutations.setSelectedDeviceId(selectedDevice.id as number);
    }

    async attemptRestoreSelectedCard(): Promise<void> {
        const selectedCard = await db.selected.get("access-control-card");

        if (!selectedCard) return;

        this.mutations.setSelectedCardNumber(selectedCard.id as number);
    }

    async initialize(): Promise<void> {
        this.mutations.setIsInitialized(true);
    }

    async rehydrateStore(): Promise<void> {
        await this.actions.attemptRestoreSelectedDevice();
        await this.actions.attemptRestoreSelectedCard();
    }

    async selectDevice(deviceId: number): Promise<void> {
        await db.selected.put({id: deviceId}, "access-control-device");

        this.mutations.setSelectedDeviceId(deviceId);
        this.mutations.setSelectedCardNumber(null);
        this.mutations.setSelectedEventsPageNumber(1);
    }

    async selectCard(cardNumber: number): Promise<void> {
        await db.selected.put({id: cardNumber}, "access-control-card");

        this.mutations.setSelectedCardNumber(cardNumber);
    }

    async selectDoor(doorId: number): Promise<void> {
        this.mutations.setSelectedDoorId(doorId);
    }

    async indexDevices(buildingId: string): Promise<void> {
        const cachedExpiresAt = this.state.devicesCacheExpiresAtByBuildingId[buildingId];
        const cachedDevices = this.state.devicesByBuildingId[buildingId];
        const cachedStatus = this.state.accessControlStatusByBuildingId[buildingId];

        if (cachedExpiresAt && cachedExpiresAt > Date.now() && cachedDevices && cachedStatus) {
            this.mutations.setDevices(cachedDevices);
            this.mutations.setAccessControlStatus(cachedStatus);
            return;
        }

        this.mutations.setAccessControlStatus(AccessControlStatus.Loading);

        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 403) {
            this.mutations.setDevices([]);
            this.mutations.setAccessControlStatus(AccessControlStatus.Forbidden);
            return;
        }

        if (response.status === 200) {
            const devices = response.data.map((data: IAccessControlDeviceData) => new AccessControlDeviceData(data));
            const status = devices.length > 0 ? AccessControlStatus.Ready : AccessControlStatus.Empty;

            this.mutations.setDevices(devices);
            this.mutations.setAccessControlStatus(status);
            this.mutations.setDevicesCache({
                buildingId,
                devices,
                status,
                expiresAt: Date.now() + 10 * 60 * 1000 // 10 minutes
            });
            return;
        }

        if (response.status === 400) {
            const errorResponse = response.data as AccessControlErrorResponse;

            if (errorResponse.code === "not_implemented") {
                this.mutations.setDevices([]);
                this.mutations.setAccessControlStatus(AccessControlStatus.NotImplemented);
                return;
            }
        }
        this.mutations.setDevices([]);
        this.mutations.setAccessControlStatus(AccessControlStatus.Failed);
        await this.actions.handleErrorResponse(response);
    }

    async showDevice(payload: AccessControlDevicePayload): Promise<void> {
        const {buildingId, deviceId} = payload;
        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            const device = new AccessControlDeviceData(response.data);

            this.mutations.setDeviceDetails(device);
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async updateDevice(payload: AccessControlUpdateDevicePayload): Promise<AxiosResponse | null> {
        const {buildingId, deviceId, request} = payload;
        const response = await esquireClient
            .put(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}`, request)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return null;
        }

        if (await this.actions.handleAuthStatus(response.status)) return response;

        if (response.status === 200) {
            const device = new AccessControlDeviceData(response.data);

            this.mutations.setDeviceDetails(device);

            await this.rootContext.actions.message({
                message: "Device name updated.",
                type: "success",
                duration: 3000,
                showClose: true
            });
            return response;
        }

        await this.actions.handleErrorResponse(response);
        return response;
    }

    async indexDoors(payload: AccessControlDevicePayload): Promise<void> {
        const {buildingId, deviceId} = payload;
        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/doors`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            const doors = response.data.map((data: IAccessControlDoorData) => new AccessControlDoorData(data));

            this.mutations.setDoorsForDevice({deviceId, doors});
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async showDoor(payload: AccessControlDoorPayload): Promise<void> {
        const {buildingId, deviceId, doorId} = payload;
        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/doors/${doorId}`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            const door = new AccessControlDoorData(response.data);

            this.mutations.setDoorDetails({deviceId, door});
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async updateDoor(payload: AccessControlUpdateDoorPayload): Promise<AxiosResponse | null> {
        const {buildingId, deviceId, doorId, request} = payload;
        const response = await esquireClient
            .put(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/doors/${doorId}`, request)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return null;
        }

        if (await this.actions.handleAuthStatus(response.status)) return response;

        if (response.status === 200) {
            const door = new AccessControlDoorData(response.data);

            this.mutations.setDoorDetails({deviceId, door});

            await this.rootContext.actions.message({
                message: "Door name updated.",
                type: "success",
                duration: 3000,
                showClose: true
            });
            return response;
        }

        await this.actions.handleErrorResponse(response);
        return response;
    }

    async indexCards(payload: AccessControlDevicePayload): Promise<void> {
        const {buildingId, deviceId} = payload;
        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/cards`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            this.mutations.setCardNumbersForDevice({
                deviceId,
                cardNumbers: response.data as number[]
            });
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async showCard(payload: AccessControlCardPayload): Promise<void> {
        const {buildingId, deviceId, cardNumber} = payload;
        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/cards/${cardNumber}`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            const card = new AccessControlCardData(response.data);

            this.mutations.setCardDetails({deviceId, card});
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async createCard(payload: AccessControlCreateCardPayload): Promise<AxiosResponse | null> {
        const {buildingId, deviceId, request} = payload;
        const response = await esquireClient
            .post(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/cards`, request)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return null;
        }

        if (await this.actions.handleAuthStatus(response.status)) return response;

        if (response.status === 201) {
            const card = new AccessControlCardData(response.data);
            this.actions.upsertCard({deviceId, card});
            return response;
        }
        if (response.status === 422) {
            return response;
        }
        await this.actions.handleErrorResponse(response);
        return response;
    }

    async updateCard(payload: AccessControlUpdateCardPayload): Promise<AxiosResponse | null> {
        const {buildingId, deviceId, cardNumber, request} = payload;
        const response = await esquireClient
            .put(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/cards/${cardNumber}`, request)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return null;
        }

        if (await this.actions.handleAuthStatus(response.status)) return response;

        if (response.status === 200) {
            const card = new AccessControlCardData(response.data);
            this.actions.upsertCard({deviceId, card});
            return response;
        }
        if (response.status === 422) {
            return response;
        }
        await this.actions.handleErrorResponse(response);
        return response;
    }

    async deleteCard(payload: AccessControlCardPayload): Promise<void> {
        const {buildingId, deviceId, cardNumber} = payload;
        const response = await esquireClient
            .delete(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/cards/${cardNumber}`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 204) {
            this.mutations.removeCard({deviceId, cardNumber});
            this.rootContext.modules.unit.mutations.removeAccessControlDeviceCard(cardNumber);
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async indexEvents(payload: AccessControlEventsPayload): Promise<void> {
        const {buildingId, deviceId, page, perPage} = payload;
        const response = await esquireClient
            .get(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/events`, {
                params: {
                    page,
                    per_page: perPage
                }
            })
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            const eventsPage = new AccessControlEventPageData(response.data);

            this.mutations.setEventsPage({deviceId, page, eventsPage});
            this.mutations.setSelectedEventsPageNumber(page);
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async setDoorPasscodes(payload: AccessControlDoorPasscodesPayload): Promise<void> {
        const {buildingId, deviceId, doorId, request} = payload;
        const response = await esquireClient
            .post(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/doors/${doorId}/set-passcodes`, request)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            new AccessControlDoorPasscodesData(response.data);

            await this.rootContext.actions.message({
                message: "Door passcode updated.",
                type: "success",
                duration: 3000,
                showClose: true
            });
            return;
        }

        await this.actions.handleErrorResponse(response);
    }

    async unlockDoor(payload: AccessControlDoorPayload): Promise<void> {
        const {buildingId, deviceId, doorId} = payload;
        const response = await esquireClient
            .post(`domotics/buildings/${buildingId}/access-control-devices/${deviceId}/doors/${doorId}/unlock`)
            .catch((e: AxiosError) => e.response);

        if (!response) {
            await this.rootContext.actions.notifyNetworkError();
            return;
        }

        if (await this.actions.handleAuthStatus(response.status)) return;

        if (response.status === 200) {
            const unlockResult = new AccessControlDoorUnlockData(response.data);
            const message = unlockResult.opened ? "Door unlocked." : "Door unlock failed.";

            await this.rootContext.actions.message({
                message,
                type: unlockResult.opened ? "success" : "warning",
                duration: 3000,
                showClose: true
            });
            return;
        }

        await this.actions.handleErrorResponse(response);
    }
}
